import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { ErrorBox } from '@/components/ErrorBox';
import { resendVerification } from '@/query/auth';

function RegisterSucessPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const email = (location.state as { email?: string } | null)?.email || '';
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown(false), 60000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  async function resend() {
    if (!email.trim() || inFlight.current || cooldown) return;
    inFlight.current = true;
    setSending(true);
    setSent(false);
    setError('');
    try {
      await resendVerification(email.trim());
      setSent(true);
      setCooldown(true);
    } catch {
      setError(t('pages.auth.register_success.resend_error'));
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }

  return (
    <div className="h-full overflow-hidden md:flex md:items-center">
      <div className="m-8 flex h-full flex-col justify-center gap-8 overflow-hidden text-center md:w-1/2 md:items-center md:overflow-auto">
        <div className="flex h-full overflow-hidden md:w-1/2 md:items-center md:overflow-auto">
          <div className="m-auto flex w-full max-w-[420px] flex-col max-md:h-full max-md:overflow-hidden">
            <div className="space-y-6 px-5 pt-8 max-md:flex-grow max-md:overflow-auto md:space-y-8">
              <img
                className="h-12 w-12 md:h-16 md:w-16"
                src="/TEO_logo.svg"
                alt="Logo"
              />
              <h1 className="text-3xl-semibold text-left">
                {t('pages.auth.forgot_password.got_mail_title')}
              </h1>
              <div className="space-y-6 md:space-y-8">
                <p className="text-left text-sm text-grey-05">
                  {t('pages.auth.register_success.description')}
                </p>
                <p className="text-left text-sm text-grey-05">
                  {t('pages.auth.register_success.didnt_receive_msg')}
                </p>
                <form
                  className="space-y-4 text-left"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void resend();
                  }}
                >
                  {error && <ErrorBox text={error} />}
                  {sent && (
                    <p role="status" className="text-sm text-grey-05">
                      {t('pages.auth.register_success.resend_sent')}
                    </p>
                  )}
                  <button
                    type="submit"
                    className="text-sm text-tint-blue hover:underline disabled:cursor-wait disabled:opacity-50"
                    disabled={sending || cooldown || !email.trim()}
                  >
                    {t(
                      sending
                        ? 'pages.auth.register_success.resend_sending'
                        : 'pages.auth.forgot_password.resend_link'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="w-1/2 max-md:hidden md:flex md:h-[100vh] md:items-center md:justify-center">
        <img
          className="h-full w-full object-cover"
          role="presentation"
          src="/images/interface/auth/login.jpg"
          alt="login_image"
        />
      </div>
    </div>
  );
}

export default RegisterSucessPage;
